/**
 * Coups de pouce — Attendu 899 « Additionner, soustraire, multiplier et diviser
 * pour résoudre des problèmes et contrôler la vraisemblance de son résultat ».
 * Une entrée par generator_key (cf. gen_899.js).
 *
 * Valeurs des exemples HORS plages du générateur (qui tire a ∈ [110..1023],
 * b ∈ [9..25], q ∈ [12..40]) : 47 objets transportés 6 par 6 → 7 paquets pleins
 * et un reste de 5. L'aide ne peut donc jamais servir la réponse.
 *
 * Fiches TOUT EN SVG (revue 2026-08-01) : aucune ligne de texte hors figure,
 * la règle s'écrit à la fin de l'animation. Trois exigences utilisateur :
 *   - l'animation se FIGE une fois terminée (pas de boucle),
 *   - un bouton « ↻ Revoir » la relance,
 *   - la règle du reste conclut la première figure.
 * D'où le choix de SMIL (<animate>) plutôt que des @keyframes CSS comme
 * 650/651 : le SVG est injecté par dangerouslySetInnerHTML, aucun JS ne s'y
 * exécute — seul `begin="0s;bouton.click"` permet un rejeu, et `fill="freeze"`
 * un arrêt sur la dernière image. Le HTML parser crée bien les <animate> dans
 * le namespace SVG, l'horloge démarre à l'insertion.
 */

/**
 * Étape d'apparition : le groupe reste transparent jusqu'à `p` (fraction du
 * cycle), monte en 3 % puis GÈLE. Tous les groupes partagent le même `begin`,
 * donc un clic sur le bouton les remet tous à zéro et rejoue la cascade.
 */
const apparition = (p, btn, duree = '7.6s') =>
    `<animate attributeName="opacity" values="0;0;1;1" ` +
    `keyTimes="0;${p.toFixed(2)};${(p + 0.03).toFixed(2)};1" ` +
    `dur="${duree}" begin="0s;${btn}.click" fill="freeze"/>`;

/** Groupe animé. opacity="1" en attribut = fallback si SMIL est indisponible. */
const etape = (p, btn, contenu) =>
    `<g class="anim" opacity="1">${apparition(p, btn)}${contenu}</g>`;

const bouton = (id, x, y) =>
    `<g id="${id}" style="cursor:pointer">` +
    `<title>Rejouer l'animation</title>` +
    `<rect x="${x}" y="${y}" width="76" height="21" rx="10.5" fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.2"/>` +
    `<text x="${x + 38}" y="${y + 15}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">↻ Revoir</text>` +
    `</g>`;

// Le seul CSS restant : respecter prefers-reduced-motion. Une règle !important
// de l'auteur prime sur la valeur animée dans les moteurs testés ; en cas
// d'échec l'élève voit simplement l'animation, jamais une figure vide.
const STYLE_REDUCED = `<style>@media (prefers-reduced-motion: reduce) { .anim { opacity: 1 !important; } }</style>`;

// ─── Page 1 — le groupement en paquets ───────────────────────
// Grille 8 colonnes × 6 lignes remplie colonne par colonne : 7 colonnes pleines
// (42 jetons) + une 8e colonne à 5 jetons, dont la place manquante est tracée
// en pointillé (le paquet n'est PAS complet, c'est tout le propos).
const BTN_A = 'cp899rejouerA';
const X0 = 26, Y0 = 40, DX = 33, DY = 20, R = 6.5;
const cx = (i) => X0 + i * DX;
const cy = (j) => Y0 + j * DY;

const jetons = [...Array(8)].map((_, i) =>
    [...Array(i === 7 ? 5 : 6)].map((__, j) =>
        `<circle cx="${cx(i)}" cy="${cy(j)}" r="${R}" fill="#cbd5e1" stroke="#94a3b8" stroke-width="1"/>`,
    ).join(''),
).join('');

const placeVide = `<circle cx="${cx(7)}" cy="${cy(5)}" r="${R}" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-dasharray="3 3"/>`;

const cadres = [0.05, 0.10, 0.15, 0.20, 0.25, 0.30, 0.35].map((p, i) =>
    etape(p, BTN_A,
        `<rect x="${cx(i) - 11}" y="29" width="22" height="122" rx="6" fill="none" stroke="#1E3A8A" stroke-width="2"/>`),
).join('');

const resteOrange = [...Array(5)].map((_, j) =>
    `<circle cx="${cx(7)}" cy="${cy(j)}" r="${R}" fill="#f97316" stroke="#ea580c" stroke-width="1"/>`,
).join('');

const SVG_PAQUETS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 240" width="290" height="232" role="img"
     aria-label="47 jetons rangés en colonnes de 6. Sept colonnes complètes s'encadrent une à une : 7 paquets de 6 font 42 jetons. Les 5 jetons de la dernière colonne restent seuls et deviennent orange : c'est le reste, et il manque une place pour compléter le paquet. Grouper ainsi, c'est faire une division : 47 divisé par 6 donne 7 et il reste 5. On écrit 47 = 6 × 7 + 5, et le reste est toujours plus petit que le nombre par paquet. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="20" font-size="13" font-weight="bold" fill="#0f172a">47 objets, 6 par voyage</text>
  ${bouton(BTN_A, 218, 6)}
  ${jetons}
  ${placeVide}
  ${cadres}
  ${etape(0.42, BTN_A, `<text x="14" y="170" font-size="13" fill="#1E3A8A">7 paquets de 6 = 42</text>`)}
  ${etape(0.50, BTN_A, `${resteOrange}<text x="${cx(7)}" y="170" text-anchor="middle" font-size="13" font-weight="bold" fill="#f97316">reste : 5</text>`)}
  ${etape(0.58, BTN_A, `<text x="14" y="192" font-size="13" fill="#334155">Grouper ainsi, c'est <tspan font-weight="bold" fill="#1E3A8A">diviser 47 par 6</tspan>.</text>`)}
  ${etape(0.66, BTN_A, `<text x="14" y="214" font-size="15" font-weight="bold" fill="#0f172a">47 ÷ 6 → 47 = 6 × 7 + <tspan fill="#f97316">5</tspan></text>`)}
  ${etape(0.76, BTN_A, `<text x="14" y="233" font-size="12.5" fill="#334155">Le reste est toujours plus petit que <tspan font-weight="bold">6</tspan>.</text>`)}
</svg>`.trim();

// ─── Page 2 — le voyage supplémentaire ───────────────────────
// 8 caisses en 2 rangées de 4 : les 7 premières pleines (6 points), la 8e à 5
// points avec une place vide en pointillé, encadrée en orange.
const BTN_B = 'cp899rejouerB';
// CY0 = 46 : les numéros de caisse se posent 4 px au-dessus du cadre, il leur
// faut de l'air sous la ligne de titre (chevauchement signalé en revue).
const CX0 = 18, CY0 = 46, CDX = 68, CDY = 58, CW = 60, CH = 42;
const bx = (c) => CX0 + (c % 4) * CDX;
const by = (c) => CY0 + Math.floor(c / 4) * CDY;

const points = (c, n) => [...Array(6)].map((_, k) => {
    const x = bx(c) + 15 + (k % 3) * 15;
    const y = by(c) + 15 + Math.floor(k / 3) * 16;
    return k < n
        ? `<circle cx="${x}" cy="${y}" r="5" fill="#cbd5e1" stroke="#94a3b8" stroke-width="1"/>`
        : `<circle cx="${x}" cy="${y}" r="5" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-dasharray="3 3"/>`;
}).join('');

const caisses = [0.04, 0.09, 0.14, 0.19, 0.24, 0.29, 0.34, 0.44].map((p, c) => {
    const dernier = c === 7;
    const couleur = dernier ? '#f97316' : '#1E3A8A';
    return etape(p, BTN_B,
        `<rect x="${bx(c)}" y="${by(c)}" width="${CW}" height="${CH}" rx="6" fill="none" stroke="${couleur}" stroke-width="${dernier ? 2.5 : 2}"/>` +
        `<text x="${bx(c) + 4}" y="${by(c) - 4}" font-size="11" font-weight="bold" fill="${couleur}">${c + 1}</text>` +
        points(c, dernier ? 5 : 6));
}).join('');

const SVG_VOYAGES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 210" width="290" height="204" role="img"
     aria-label="Huit voyages apparaissent l'un après l'autre. Les sept premiers transportent 6 objets chacun : c'est le quotient de la division. Le huitième, encadré en orange, n'en transporte que 5 : c'est le reste, et une place y demeure vide. Il faut donc un voyage de plus que le quotient : 7 + 1 = 8 voyages, et le dernier voyage transporte le reste. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">Chaque voyage : 6 objets</text>
  ${bouton(BTN_B, 218, 3)}
  ${caisses}
  ${etape(0.52, BTN_B, `<text x="${bx(7) + CW / 2}" y="${by(7) + CH + 15}" text-anchor="middle" font-size="12" font-weight="bold" fill="#f97316">5 objets</text>`)}
  ${etape(0.62, BTN_B, `<text x="14" y="184" font-size="15" font-weight="bold" fill="#0f172a">7 + 1 = <tspan fill="#f97316">8 voyages</tspan></text>`)}
  ${etape(0.74, BTN_B, `<text x="14" y="204" font-size="12.5" fill="#334155">Le dernier voyage transporte <tspan font-weight="bold">le reste</tspan>.</text>`)}
</svg>`.trim();

// ─── Carte « deux étapes » — modèle en barres ────────────────
// Total 96 €, appareil 21 €, 5 articles à 15 €. Hors plages du générateur, qui
// tire 2 à 4 articles, un appareil de 29,15 € à 79,95 € et des prix à deux
// décimales : l'exemple ne peut jamais coïncider avec l'exercice.
const BTN_C = 'cp899rejouerC', BTN_D = 'cp899rejouerD';
const BAR_X = 20, BAR_W = 260, PX_EURO = BAR_W / 96;   // 96 € sur toute la largeur
const W_APP = Math.round(21 * PX_EURO);                // segment de l'appareil
const W_ART = BAR_W - W_APP;                           // segment des 5 articles

const SVG_ENLEVER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 184" width="290" height="178" role="img"
     aria-label="Une barre représente les 96 € payés en tout. Un premier morceau, en orange, vaut 21 € : c'est le sac à dos. Le reste de la barre, en bleu, représente les 5 cahiers ensemble. On enlève le morceau du sac à dos et il reste une barre de 75 € : 96 − 21 = 75 €. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">96 € payés en tout</text>
  ${bouton(BTN_C, 218, 6)}
  <rect x="${BAR_X}" y="34" width="${BAR_W}" height="30" rx="4" fill="#f8fafc" stroke="#94a3b8" stroke-width="1.5"/>
  ${etape(0.06, BTN_C,
    `<rect x="${BAR_X}" y="34" width="${W_APP}" height="30" rx="4" fill="#fed7aa" stroke="#f97316" stroke-width="2"/>` +
    `<text x="${BAR_X + W_APP / 2}" y="53" text-anchor="middle" font-size="11" font-weight="bold" fill="#9a3412">21 €</text>` +
    `<text x="${BAR_X + W_APP / 2}" y="78" text-anchor="middle" font-size="10" fill="#9a3412">sac à dos</text>`)}
  ${etape(0.18, BTN_C,
    `<rect x="${BAR_X + W_APP}" y="34" width="${W_ART}" height="30" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="2"/>` +
    `<text x="${BAR_X + W_APP + W_ART / 2}" y="53" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">les 5 cahiers</text>`)}
  ${etape(0.36, BTN_C,
    `<path d="M ${BAR_X + W_APP + 10} 88 L ${BAR_X + 4} 104" stroke="#f97316" stroke-width="2" fill="none" stroke-dasharray="4 3"/>` +
    `<text x="${BAR_X + W_APP + 18}" y="94" font-size="11.5" fill="#9a3412">on enlève le sac à dos</text>`)}
  ${etape(0.52, BTN_C,
    `<rect x="${BAR_X}" y="110" width="${W_ART}" height="30" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="2"/>` +
    `<text x="${BAR_X + W_ART / 2}" y="129" text-anchor="middle" font-size="12" font-weight="bold" fill="#1E3A8A">75 €</text>`)}
  ${etape(0.68, BTN_C,
    `<text x="14" y="168" font-size="15" font-weight="bold" fill="#0f172a">96 − 21 = <tspan fill="#1E3A8A">75 €</tspan></text>`)}
</svg>`.trim();

const PARTS = [...Array(5)].map((_, k) => {
    const w = W_ART / 5;
    const x = BAR_X + k * w;
    return etape(0.08 + k * 0.08, BTN_D,
        `<rect x="${x.toFixed(1)}" y="34" width="${(w - 2).toFixed(1)}" height="30" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${(x + w / 2 - 1).toFixed(1)}" y="53" text-anchor="middle" font-size="10" font-weight="bold" fill="#1E3A8A">15 €</text>`);
}).join('');

const SVG_PARTAGER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 166" width="290" height="160" role="img"
     aria-label="La barre de 75 €, qui représente les 5 cahiers ensemble, se découpe en 5 parts égales. Chaque part vaut 15 € : 75 divisé par 5 égale 15. Un cahier coûte donc 15 €. On enlève d'abord, on partage ensuite. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">75 € pour 5 cahiers</text>
  ${bouton(BTN_D, 218, 6)}
  <rect x="${BAR_X}" y="34" width="${W_ART}" height="30" rx="4" fill="#f8fafc" stroke="#94a3b8" stroke-width="1.5"/>
  ${PARTS}
  ${etape(0.56, BTN_D,
    `<text x="14" y="96" font-size="15" font-weight="bold" fill="#0f172a">75 ÷ 5 = <tspan fill="#1E3A8A">15 €</tspan></text>`)}
  ${etape(0.68, BTN_D,
    `<text x="14" y="120" font-size="12.5" fill="#334155">Un cahier coûte <tspan font-weight="bold">15 €</tspan>.</text>`)}
  ${etape(0.78, BTN_D,
    `<text x="14" y="144" font-size="12.5" fill="#334155">On <tspan font-weight="bold">enlève</tspan> d'abord, on <tspan font-weight="bold">partage</tspan> ensuite.</text>`)}
</svg>`.trim();

/** Fiche « deux étapes » : retirer l'appareil, puis partager le reste. */
const FICHE_DEUX_ETAPES = {
    pages: [
        { titre: "D'abord, on enlève", svg: SVG_ENLEVER },
        { titre: 'Ensuite, on partage', svg: SVG_PARTAGER },
    ],
};

// ─── Carte « haies » — compter les intervalles ───────────────
// Course de 60 m à 5 haies (donc 4 intervalles), première haie à 10 m,
// espacement 8 m. Le générateur tire TOUJOURS 110 m et 10 haies (9 intervalles),
// première haie entre 12,50 et 14,50 m, espacement entre 8,50 et 9,50 m :
// aucune valeur ne peut coïncider, et le nombre d'intervalles diffère — l'élève
// doit refaire le raisonnement au lieu de recopier « 9 ».
const BTN_E = 'cp899rejouerE', BTN_F = 'cp899rejouerF';
const H_X0 = 30, H_DX = 52, H_Y = 74, H_H = 14;     // 5 haies espacées de 52 px

/**
 * Haie BASSE (revue 2026-08-01, retour utilisateur) : dessinée haute, elle
 * laissait croire que sa hauteur entrait dans le calcul — c'est justement la
 * donnée inutile de l'énoncé. Elle ne marque plus qu'une position.
 * La base `y` est un paramètre : les deux pages n'ont pas la même piste.
 */
const haie = (x, y, couleur = '#1E3A8A') =>
    `<line x1="${x}" y1="${y}" x2="${x}" y2="${y - H_H}" stroke="${couleur}" stroke-width="2.5"/>` +
    `<line x1="${x - 6}" y1="${y - H_H}" x2="${x + 6}" y2="${y - H_H}" stroke="${couleur}" stroke-width="2.5"/>`;

const hx = (k) => H_X0 + k * H_DX;

/** Cote horizontale (segment à bornes) sous la piste, avec son étiquette. */
const cote = (x1, x2, y, label, couleur) =>
    `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${couleur}" stroke-width="2"/>` +
    `<line x1="${x1}" y1="${y - 4}" x2="${x1}" y2="${y + 4}" stroke="${couleur}" stroke-width="2"/>` +
    `<line x1="${x2}" y1="${y - 4}" x2="${x2}" y2="${y + 4}" stroke="${couleur}" stroke-width="2"/>` +
    `<text x="${(x1 + x2) / 2}" y="${y + 17}" text-anchor="middle" font-size="11" font-weight="bold" fill="${couleur}">${label}</text>`;

const intervalles = [0.10, 0.20, 0.30, 0.40].map((p, k) =>
    etape(p, BTN_E,
        `<line x1="${hx(k) + 4}" y1="${H_Y + 14}" x2="${hx(k + 1) - 4}" y2="${H_Y + 14}" stroke="#f97316" stroke-width="2"/>` +
        `<line x1="${hx(k) + 4}" y1="${H_Y + 10}" x2="${hx(k) + 4}" y2="${H_Y + 18}" stroke="#f97316" stroke-width="2"/>` +
        `<line x1="${hx(k + 1) - 4}" y1="${H_Y + 10}" x2="${hx(k + 1) - 4}" y2="${H_Y + 18}" stroke="#f97316" stroke-width="2"/>` +
        `<text x="${(hx(k) + hx(k + 1)) / 2}" y="${H_Y + 32}" text-anchor="middle" font-size="12" font-weight="bold" fill="#f97316">${k + 1}</text>`),
).join('');

const SVG_INTERVALLES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 168" width="290" height="162" role="img"
     aria-label="Cinq haies sont alignées sur une piste. Les intervalles qui les séparent s'illuminent un à un et sont numérotés 1, 2, 3 et 4. Il y a donc 5 haies mais seulement 4 intervalles : entre n haies, il y a toujours n moins 1 intervalles. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">5 haies sur la piste</text>
  ${bouton(BTN_E, 218, 6)}
  <line x1="14" y1="${H_Y}" x2="286" y2="${H_Y}" stroke="#94a3b8" stroke-width="1.5"/>
  ${[0, 1, 2, 3, 4].map(k => haie(hx(k), H_Y)).join('')}
  ${[0, 1, 2, 3, 4].map(k => `<text x="${hx(k)}" y="${H_Y - H_H - 6}" text-anchor="middle" font-size="10" fill="#64748b">${k + 1}</text>`).join('')}
  ${intervalles}
  ${etape(0.54, BTN_E,
    `<text x="14" y="140" font-size="14" font-weight="bold" fill="#0f172a">5 haies, mais <tspan fill="#f97316">4 intervalles</tspan></text>`)}
  ${etape(0.66, BTN_E,
    `<text x="14" y="160" font-size="12.5" fill="#334155">Toujours <tspan font-weight="bold">un intervalle de moins</tspan>.</text>`)}
</svg>`.trim();

// Piste de 60 m sur 260 px : dernière haie à 42 m, arrivée à 60 m.
const P_X0 = 20, P_W = 260, PX_M = P_W / 60, P_Y = 62;
const pxm = (m) => P_X0 + m * PX_M;

// Toutes les MESURES sont des cotes SOUS la piste (revue 2026-08-01) : rien
// au-dessus de la ligne ne porte de longueur, sinon une étiquette posée près
// d'une haie se lit comme sa hauteur — qui est justement la donnée inutile.
// Les deux termes de la somme sont deux cotes bout à bout : l'addition se voit.
const SVG_ARRIVEE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 162" width="290" height="157" role="img"
     aria-label="Une piste de 60 m, du départ à la ligne d'arrivée. Sous la piste, une première cote va du départ à la première haie : 10 m. Une deuxième cote, bout à bout avec la première, couvre les 4 intervalles de 8 m entre la première et la dernière haie. En ajoutant les deux, 10 plus 4 fois 8 égale 42 m : c'est la position de la dernière haie. Le morceau qui reste jusqu'à l'arrivée s'épaissit en orange : 60 moins 42 égale 18 m. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">Une course de 60 m</text>
  ${bouton(BTN_F, 218, 6)}
  <line x1="${P_X0}" y1="${P_Y}" x2="${P_X0 + P_W}" y2="${P_Y}" stroke="#94a3b8" stroke-width="1.5"/>
  <line x1="${P_X0}" y1="${P_Y - 14}" x2="${P_X0}" y2="${P_Y + 6}" stroke="#0f172a" stroke-width="2"/>
  <text x="14" y="${P_Y - 20}" font-size="10" fill="#64748b">départ</text>
  <line x1="${P_X0 + P_W}" y1="${P_Y - 14}" x2="${P_X0 + P_W}" y2="${P_Y + 6}" stroke="#0f172a" stroke-width="2"/>
  <text x="${P_X0 + P_W}" y="${P_Y - 20}" text-anchor="end" font-size="10" fill="#64748b">arrivée</text>
  ${etape(0.06, BTN_F,
    haie(pxm(10), P_Y) + cote(P_X0, pxm(10), P_Y + 20, '10 m', '#1E3A8A'))}
  ${etape(0.22, BTN_F,
    [1, 2, 3, 4].map(k => haie(pxm(10 + 8 * k), P_Y)).join('') +
    cote(pxm(10), pxm(42), P_Y + 20, '4 × 8 m', '#1E3A8A'))}
  ${etape(0.40, BTN_F,
    `<text x="14" y="126" font-size="14" font-weight="bold" fill="#0f172a">10 + 4 × 8 = <tspan fill="#1E3A8A">42 m</tspan></text>`)}
  ${etape(0.58, BTN_F,
    `<line x1="${pxm(42)}" y1="${P_Y}" x2="${pxm(60)}" y2="${P_Y}" stroke="#f97316" stroke-width="5"/>` +
    cote(pxm(42), pxm(60), P_Y + 20, '?', '#f97316'))}
  ${etape(0.72, BTN_F,
    `<text x="14" y="150" font-size="14" font-weight="bold" fill="#0f172a">60 − 42 = <tspan fill="#f97316">18 m</tspan></text>`)}
</svg>`.trim();

/** Fiche « haies » : compter les intervalles, puis la distance à l'arrivée. */
const FICHE_HAIES = {
    pages: [
        { titre: 'Compter les intervalles', svg: SVG_INTERVALLES },
        { titre: "Jusqu'à l'arrivée", svg: SVG_ARRIVEE },
    ],
};

/**
 * Fiche « division euclidienne ». PARTAGÉE par les trois formats de la carte
 * voyages (revue 2026-08-01) : même raisonnement partout, l'élève écrit la
 * valeur, la choisit parmi des étiquettes ou parmi quatre propositions. Une
 * seule source de vérité — toute retouche des animations profite aux trois.
 * Les deux pages couvrent les deux questions du QCM (nombre de voyages =
 * quotient + 1, charge du dernier voyage = reste), d'où le partage plutôt
 * qu'un routage par sousType.
 */
const FICHE_DIVISION = {
    pages: [
        { titre: 'Des paquets, et un reste', svg: SVG_PAQUETS },
        { titre: 'Un voyage de plus', svg: SVG_VOYAGES },
    ],
};

// ─── Carte « vraisemblance » — estimer par ordre de grandeur ─
// 5 articles à 3,05 € et un sac à 9,90 € (puis 10,10 €), budget 30 € puis 25 €.
// Le générateur tire 2 à 4 articles frôlant 1 ou 2 €, un second article sous
// 5 € et un budget de 3 à 13 € : aucune valeur ne peut coïncider.
// Les deux pages suivent les deux régimes du gen : « clair » (les arrondis
// suffisent) et « limite » (ils tombent pile sur le budget, il faut alors
// regarder le SENS de chaque écart).
const BTN_G = 'cp899rejouerG', BTN_H = 'cp899rejouerH';
const V_X0 = 20, V_W = 200, V_Y = 92;                  // barres de comparaison
const vw = (euros, total) => Math.round(V_W * euros / total);

/** Ligne « prix → arrondi ». Colonnes fixes : le prix le plus long
 *  (« 5 × 3,05 € ») s'arrête à x ≈ 93, la flèche est donc posée à 100. */
const arrondi = (y, prix, vers) =>
    `<text x="24" y="${y}" font-size="12.5" fill="#334155">${prix}</text>` +
    `<text x="100" y="${y}" font-size="12.5" fill="#94a3b8">→</text>` +
    `<text x="120" y="${y}" font-size="12.5" font-weight="bold" fill="#1E3A8A">${vers}</text>`;

/** Barre horizontale étiquetée (comparaison total / budget). */
const barre = (y, largeur, couleur, fond, label) =>
    `<rect x="${V_X0}" y="${y}" width="${largeur}" height="20" rx="4" fill="${fond}" stroke="${couleur}" stroke-width="2"/>` +
    `<text x="${V_X0 + largeur + 8}" y="${y + 15}" font-size="12" font-weight="bold" fill="${couleur}">${label}</text>`;

const SVG_ESTIMER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 170" width="290" height="164" role="img"
     aria-label="Cinq crayons à 3,05 € et un sac à 9,90 €, avec 30 € en poche. Chaque prix est arrondi à l'euro le plus proche : 3,05 devient 3, et 9,90 devient 10. Cinq fois 3 font 15, plus 10 font 25 €. La barre du total, 25 €, est nettement plus courte que celle du budget, 30 € : l'achat est possible sans avoir posé la moindre opération exacte. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">5 crayons + 1 sac, 30 €</text>
  ${bouton(BTN_G, 218, 6)}
  ${etape(0.06, BTN_G, arrondi(42, '5 × 3,05 €', '5 × 3 = 15 €'))}
  ${etape(0.20, BTN_G, arrondi(62, '9,90 €', '10 €'))}
  ${etape(0.36, BTN_G, `<text x="24" y="82" font-size="13" font-weight="bold" fill="#0f172a">15 + 10 = 25 €</text>`)}
  ${etape(0.52, BTN_G, barre(V_Y + 8, vw(25, 30), '#16a34a', '#dcfce7', '25 €'))}
  ${etape(0.62, BTN_G, barre(V_Y + 34, vw(30, 30), '#64748b', '#f1f5f9', '30 €'))}
  ${etape(0.76, BTN_G, `<text x="14" y="164" font-size="13" font-weight="bold" fill="#16a34a">25 € &lt; 30 € : c'est possible.</text>`)}
</svg>`.trim();

const SVG_SERRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 184" width="290" height="178" role="img"
     aria-label="Mêmes achats, mais 25 € en poche seulement : cinq crayons à 3,05 € et un sac à 10,10 €. Les arrondis donnent 15 plus 10, soit exactement 25 € : les deux barres sont de même longueur, impossible de conclure. On regarde alors le sens de chaque écart : 3,05 c'est un peu plus que 3, donc plus de 15 pour cinq crayons, et 10,10 c'est un peu plus que 10. Tout est un peu plus, donc le total dépasse 25 € : l'achat est impossible. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">Mêmes achats, 25 €</text>
  ${bouton(BTN_H, 218, 6)}
  ${etape(0.06, BTN_H, arrondi(42, '5 × 3,05 €', '5 × 3 = 15 €'))}
  ${etape(0.16, BTN_H, arrondi(62, '10,10 €', '10 €'))}
  ${etape(0.28, BTN_H,
    barre(80, vw(25, 25), '#94a3b8', '#f1f5f9', '25 €') +
    `<text x="14" y="122" font-size="12.5" fill="#334155">Les arrondis tombent <tspan font-weight="bold">pile</tspan> sur 25 €.</text>`)}
  ${etape(0.46, BTN_H, arrondi(142, '3,05 €', 'un peu plus que 3'))}
  ${etape(0.58, BTN_H, arrondi(158, '10,10 €', 'un peu plus que 10'))}
  ${etape(0.74, BTN_H, `<text x="14" y="176" font-size="13" font-weight="bold" fill="#f97316">Tout est un peu plus : ça dépasse.</text>`)}
</svg>`.trim();

/** Fiche « vraisemblance » : arrondir, puis départager les cas serrés. */
const FICHE_VRAISEMBLANCE = {
    pages: [
        { titre: 'Arrondir pour estimer', svg: SVG_ESTIMER },
        { titre: "Quand c'est serré", svg: SVG_SERRE },
    ],
};

// ─── Carte « perimetre » — le tour, puis le côté manquant ────
// Triangle 12 / 15 / 13 cm, périmètre 40 cm. Le générateur tire des côtés de
// 3 à 10 cm pour un périmètre de 12 à 27 cm : aucune coïncidence possible.
// Inégalité triangulaire vérifiée (12+15>13, 12+13>15, 15+13>12).
const BTN_I = 'cp899rejouerI', BTN_J = 'cp899rejouerJ';
// Sommets choisis à la main pour un triangle bien ouvert et centré.
// +12 px en y : l'étiquette du sommet A touchait la ligne de titre.
const T_A = [150, 46], T_B = [40, 128], T_C = [258, 128];
const milieu = (P, Q) => [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];

/** Côté mis en évidence + sa longueur, posée du bon côté du trait. */
const coteTriangle = (P, Q, label, dx, dy, ancre) => {
    const m = milieu(P, Q);
    return `<line x1="${P[0]}" y1="${P[1]}" x2="${Q[0]}" y2="${Q[1]}" stroke="#1E3A8A" stroke-width="3.5"/>` +
        `<text x="${m[0] + dx}" y="${m[1] + dy}" text-anchor="${ancre}" font-size="12.5" font-weight="bold" fill="#1E3A8A">${label}</text>`;
};

const SVG_TOUR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 196" width="290" height="190" role="img"
     aria-label="Un triangle ABC. Ses trois côtés s'épaississent l'un après l'autre : 12 cm, puis 15 cm, puis 13 cm. En les ajoutant, 12 plus 15 plus 13 font 40 cm : c'est le périmètre, la longueur du tour de la figure. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">Le tour du triangle</text>
  ${bouton(BTN_I, 218, 6)}
  <polygon points="${T_A} ${T_B} ${T_C}" fill="none" stroke="#cbd5e1" stroke-width="2"/>
  <text x="${T_A[0]}" y="${T_A[1] - 9}" text-anchor="middle" font-size="14" font-weight="bold" fill="#334155">A</text>
  <text x="${T_B[0] - 13}" y="${T_B[1] + 14}" font-size="14" font-weight="bold" fill="#334155">B</text>
  <text x="${T_C[0] + 5}" y="${T_C[1] + 14}" font-size="14" font-weight="bold" fill="#334155">C</text>
  ${etape(0.08, BTN_I, coteTriangle(T_A, T_B, '12 cm', -8, -2, 'end'))}
  ${etape(0.22, BTN_I, coteTriangle(T_A, T_C, '15 cm', 8, -2, 'start'))}
  ${etape(0.36, BTN_I, coteTriangle(T_B, T_C, '13 cm', 0, 18, 'middle'))}
  ${etape(0.54, BTN_I, `<text x="14" y="168" font-size="14" font-weight="bold" fill="#0f172a">12 + 15 + 13 = <tspan fill="#1E3A8A">40 cm</tspan></text>`)}
  ${etape(0.70, BTN_I, `<text x="14" y="190" font-size="12.5" fill="#334155">Le <tspan font-weight="bold">périmètre</tspan>, c'est la longueur du tour.</text>`)}
</svg>`.trim();

// Barre du périmètre : 40 cm sur 260 px, découpée en 12 / 15 / 13.
const PB_X = 20, PB_W = 260, PB_Y = 44, PX_CM = PB_W / 40;
const pbx = (cm) => PB_X + cm * PX_CM;

const SVG_COTE_MANQUANT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 182" width="290" height="176" role="img"
     aria-label="Une barre représente les 40 cm du périmètre. Elle se découpe en trois morceaux : 12 cm, 15 cm et un troisième inconnu. Les deux morceaux connus s'enlèvent l'un après l'autre, et il ne reste que le troisième : 40 moins 12 moins 15 égale 13 cm. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="18" font-size="13" font-weight="bold" fill="#0f172a">Périmètre : 40 cm</text>
  ${bouton(BTN_J, 218, 6)}
  <rect x="${PB_X}" y="${PB_Y}" width="${PB_W}" height="26" rx="4" fill="#f8fafc" stroke="#94a3b8" stroke-width="1.5"/>
  ${etape(0.08, BTN_J,
    `<rect x="${pbx(0)}" y="${PB_Y}" width="${(12 * PX_CM).toFixed(1)}" height="26" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="2"/>` +
    `<text x="${pbx(6)}" y="${PB_Y + 18}" text-anchor="middle" font-size="12" font-weight="bold" fill="#1E3A8A">12</text>`)}
  ${etape(0.20, BTN_J,
    `<rect x="${pbx(12)}" y="${PB_Y}" width="${(15 * PX_CM).toFixed(1)}" height="26" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="2"/>` +
    `<text x="${pbx(19.5)}" y="${PB_Y + 18}" text-anchor="middle" font-size="12" font-weight="bold" fill="#1E3A8A">15</text>`)}
  ${etape(0.34, BTN_J,
    `<rect x="${pbx(27)}" y="${PB_Y}" width="${(13 * PX_CM).toFixed(1)}" height="26" rx="4" fill="#ffedd5" stroke="#f97316" stroke-width="2"/>` +
    `<text x="${pbx(33.5)}" y="${PB_Y + 18}" text-anchor="middle" font-size="13" font-weight="bold" fill="#f97316">?</text>`)}
  ${etape(0.50, BTN_J, `<text x="14" y="104" font-size="12.5" fill="#334155">On enlève les <tspan font-weight="bold">deux côtés connus</tspan>.</text>`)}
  ${etape(0.64, BTN_J, `<text x="14" y="132" font-size="14" font-weight="bold" fill="#0f172a">40 − 12 − 15 = <tspan fill="#f97316">13 cm</tspan></text>`)}
  ${etape(0.78, BTN_J, `<text x="14" y="160" font-size="12.5" fill="#334155">Le côté manquant mesure <tspan font-weight="bold">13 cm</tspan>.</text>`)}
</svg>`.trim();

/** Fiche « périmètre » : le tour de la figure, puis le côté manquant. */
const FICHE_PERIMETRE = {
    pages: [
        { titre: "Le périmètre, c'est le tour", svg: SVG_TOUR },
        { titre: 'Retrouver un côté', svg: SVG_COTE_MANQUANT },
    ],
};

const CP_899 = {
    voyages_texte_trou_saisie: FICHE_DIVISION,
    voyages_texte_trou_drag: FICHE_DIVISION,
    voyages_qcm_unique: FICHE_DIVISION,
    deux_etapes_texte_trou_saisie: FICHE_DEUX_ETAPES,
    deux_etapes_qcm_unique: FICHE_DEUX_ETAPES,
    deux_etapes_texte: FICHE_DEUX_ETAPES,
    haies_texte_trou_saisie: FICHE_HAIES,
    haies_qcm_unique: FICHE_HAIES,
    vraisemblance_qcm_unique: FICHE_VRAISEMBLANCE,
    vraisemblance_tableau_vf: FICHE_VRAISEMBLANCE,
    perimetre_texte: FICHE_PERIMETRE,
    perimetre_qcm_unique: FICHE_PERIMETRE,
};

export default CP_899;
